import store from "@/store";
import { Action, config, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { Vue } from "vue-property-decorator";
import Choices from "@/store/modules/Choices";
import Decisions from "@/store/modules/Decisions";
import Modelling from "@/store/modules/Modelling";
import Viewpoints from "@/store/modules/Viewpoints";
import Apps from '@/store/modules/Apps';
import { FactorGroup, FactorResource } from "@/api/model/FactorResource";
import { ChoiceList, ChoiceListItem, ListType, ResourceList } from '@/api/model/ResourceList';
import { VIEWPOINT_API } from '@/api/ViewpointApi';
import _ from 'lodash';
import { $cookies } from "vue/types/umd";

const appsModule = getModule(Apps);

config.rawError = true

const name = 'workspace-options';
if (module.hot) {
    if (store.hasModule(name)) {
        store.unregisterModule(name);
    }
}
const CONTRIBUTING_FACTOR_VISIBILITY_COOKIE = 'contributing-factor-visibility';
const SCORE_BAR_VISIBILITY_COOKIE = 'score-bar-visibility';
const SCORING_KEY_VISIBILITY_COOKIE = 'scoring-key-visibility';
const DESCRIPTION_VISIBILITY_COOKIE = 'description-visibility';
const SKIP_GROUP_COOKIE = 'skip-group';
const SHOW_NON_LIST_FACTORS = 'show-non-list-factors';

@Module({ dynamic: true, store, name: name, namespaced: true })
export default class WorkspaceOptions extends VuexModule {

    choiceVisibility: { [choiceId: string]: boolean } = {};
    //choiceVisibility: { [listId: string]: string } = {};

    openLeft: boolean = true;
    openRight: boolean = false;
    commentVertical: boolean = false;
    scoreBarVisibility: boolean = true;
    descriptionVisibility: boolean = true;
    contributingFactorVisibility: boolean = false;
    scoringKeyVisibility: boolean = true;
    skipGroups: boolean = true;
    showNonListFactors: boolean = false;
    viewpointSort: number = 1;
    choiceSort: number = 1;
    trimTopGroups: boolean = false;
    showIconScoring: boolean = true;
    pinnedRight: boolean = true;
    pinnedLeft: boolean = false;
    customRangeActive: boolean = true;
    commentView: string = "TAGGED";
    denominatorScoring: boolean = false;
    standardizeDenominator: boolean = false;
    showCreateChoiceDialog: boolean = false;
    showCreateViewpointDialog: boolean = false;
    showFactorData: boolean = true;
    //Expanded out values
    expansions: { [factorGroupId: string]: number } = { 'M': 0 };

    //Selection path is the order of selected values based on resource ID
    selectionPath: string[] = [];
    selectedPathItemId: string = 'M'

    //Selection path is the order of selected values based on resource ID
    draftSelectionPath: string[] = [];
    draftSelectedPathItemId: string = 'M';
    draftExpansions: { [factorGroupId: string]: number | undefined } = { 'M': 0 };

    // Help & Support
    showHelpHotSpots: boolean = false;

    rightPanelOpen: boolean = false;

    selectedElement: Element | null = null;

    activeChoiceLists: string[] = [];
    activeViewpointLists: string[] = [];
    activeFactorLists: string[] = [];

    // input box
    viewpointColumnWidth: number = 200;
    viewpointsColumnWidth: any = {};

    choicesColumnWidth: any = {};
    // input box
    choiceColumnWidth: number = 200;

    numOfCols: number = 3;

    isBarChartHorizontal: boolean = false;

    isDonutChart: boolean = false;

    isViewpointBarChartHorizontal: boolean = false;

    isFactorImportanceChartHorizontal: boolean = true;

    isHistogramChartHorizontal: boolean = false;

    // may10
    scoresVisibility: boolean = true;

    keysVisibility: boolean = true;

    commentsVisibility: boolean = true;

    autoCalc: boolean = true;

    currentUrl: any = {
        path: '',
        params: ''
    };

    choiceFirstCol: number = 400;

    viewpointFirstCol: number = 400;

    rankChoiceCol: number = 300;

    isHomepage: boolean = false;

    dialog: boolean = false;
    appEmbed: boolean = false;

    commentBottomHeight = 240;
    commentRightWidth = 360;
    leftPanelWidth = '400px';

    manageUsersTableItemPerPage = 10;

    allowGenerateViewpoints: boolean = false;

    // model builder draggable switch
    mbDraggable = true;
    commentViews = [{ name: 'Tagged', id: 'TAGGED' }, { name: 'Timeline', id: 'TIMELINE' }, { name: 'Resource', id: 'RESOURCE' } ]
    filterResources = [];
    filterTags = [];
    filterUsers = [];
    sortOrder = "Descending";
    commentSortOrder = "Ascending";

    addFactorToList: boolean = true;
    savedStateViewpoints: string[] = [];
    savedStateChoice: string = "";

    commentMenuOpen = false;

    // App Permissions
    editModel: boolean = true;
    editContent: boolean = true;
    editWeights: boolean = true;
    editSumRule: boolean = true;
    editScoringRule: boolean = true;
    editScores: boolean = true;
    editComments: boolean = true;

    // Card Editor
    chartTop: boolean = true;


    get choiceVisibilityCount() {
        let choiceIds = getModule(Choices).choiceIds;
        if (choiceIds) {
            return Object.keys(this.choiceVisibility)
                .filter(k => choiceIds.includes(k))
                .filter((k: string) => this.choiceVisibility[k])
                .length
        }
    }

    //Extract all Choice Ids
    get filteredChoiceIds(): string[] {
        let allLists = getModule(Decisions).decisionLists as { [listId: string]: ChoiceList };
        let filteredChoices: string[] = [];

        if (allLists && Object.values(this.activeChoiceLists)) {
            for (let id of Object.values(this.activeChoiceLists)) {
                if (allLists[id]) {
                    filteredChoices = filteredChoices.concat(allLists[id].choices.map((id: ChoiceListItem) => id.choiceId as string))
                } else {
                    continue;
                }

            }
        }
        return _.uniq(filteredChoices)
    }

    get currentContext() {
        switch(this.currentUrl.path) {
            case '/template/builder':
                return 'MODEL'
            case '/choice':
                return 'CHOICE'
            default:
                return 'SCORING'
        }
    }

    @Mutation
    setChartTop(val: boolean) {
        this.chartTop = val;
    }

    @Mutation
    setEditModel(val: boolean) {
        this.editModel = val;
    }

    @Mutation
    setEditContent(val: boolean) {
        this.editContent = val;
    }

    @Mutation
    setEditWeights(val: boolean) {
        this.editWeights = val;
    }

    @Mutation
    setEditSumRule(val: boolean) {
        this.editSumRule = val;
    }

    @Mutation
    setEditScoringRule(val: boolean) {
        this.editScoringRule = val;
    }

    @Mutation
    setEditScores(val: boolean) {
        this.editScores = val;
    }

    @Mutation
    setEditComments(val: boolean) {
        this.editComments = val;
    }

    // Help & Support
    @Mutation
    setShowHelpHotSpots(val: boolean) {
        this.showHelpHotSpots = val;
    }

    @Mutation
    setAllowGenerateViewpoints(val: boolean) {
        this.allowGenerateViewpoints = val;
    }

    @Mutation
    setCommentMenuOpen(open: boolean) {
        this.commentMenuOpen = open;
    }

    @Mutation
    setManageUsersTableItemPerPage(val: any) {
        this.manageUsersTableItemPerPage = val;
    }
    
    @Mutation
    setFilterResources(val: any) {
        this.filterResources = val;
    }

    @Mutation
    setFilterTags(val: any) {
        this.filterTags = val;
    }

    @Mutation
    setFilterUsers(val: any) {
        this.filterUsers = val;
    }

    @Mutation
    setSortOrder(val: string) {
        this.sortOrder = val;
    }

    @Mutation
    setCommentSortOrder(val: string) {
        this.commentSortOrder = val;
    }

    @Mutation
    setCommentRightWidth(val: number) {
        this.commentRightWidth = val;
    }

    @Mutation
    setCommentBottomHeight(val: number) {
        this.commentBottomHeight = val;
    }

    @Mutation
    setMbDraggable(val: boolean) {
        this.mbDraggable = val;
    }

    @Mutation
    setAddFactorToList(val: boolean) {
        this.addFactorToList = val;
    }

    @Mutation
    setLeftPanelWidth(width: string) {
        this.leftPanelWidth = width;
    }
    
    @Mutation
    setDialog(val: boolean) {
        this.dialog = val;
    }

    @Mutation
    setAppEmbed(val: boolean) {
        this.appEmbed = val;
    }

    @Mutation
    setIsHomepage(isHomepage: boolean){
        this.isHomepage = isHomepage;
    }

    // may10
    @Mutation
    setScoresVisibility(visible: boolean){
        this.scoresVisibility = visible;
    }

    @Mutation
    setKeysVisibility(visible: boolean) {
        this.keysVisibility = visible;
    }

    @Mutation
    setCommentsVisibility(visible: boolean) {
        this.commentsVisibility = visible;
    }

    @Mutation
    setAutoCalc(calculate: boolean){
        this.autoCalc = calculate;
    }

    // kat(may05): share dialogs url
    @Mutation
    setCurrentUrl(currentUrl: any){
        this.currentUrl = currentUrl;
    }

    @Mutation
    setIsFactorImportanceChartHorizontal(isHorizontal: boolean){
        this.isFactorImportanceChartHorizontal = isHorizontal;
    }

    @Mutation
    setIsBarChartHorizontal(isHorizontal: boolean) {
        this.isBarChartHorizontal = isHorizontal;
    }

    @Mutation
    setIsViewpointBarChartHorizontal(isHorizontal: boolean) {
        this.isViewpointBarChartHorizontal = isHorizontal;
    }

    @Mutation
    setIsDonutChart(isDonut: boolean) {
        this.isDonutChart = isDonut;
    }


    @Mutation
    setIsHistogramChartHorizontal(isHorizontal: boolean){
        this.isHistogramChartHorizontal = isHorizontal;
    }

    @Mutation
    clear() {
        this.activeChoiceLists = [];
        this.activeFactorLists = [];
        this.activeViewpointLists = [];
        this.selectedPathItemId = 'M';

    }

    @Mutation
    setCustomRangeActive(active: boolean) {
        this.customRangeActive = active;
    }

    @Mutation
    setNumOfCols(numOfCols: number){
        this.numOfCols = numOfCols;
    }

    @Mutation
    setChoiceColumnWidth(columnWidth: number) {
        this.choiceColumnWidth = columnWidth;
    }

    @Mutation
    setChoiceFirstCol(choiceFirstCol: number){
        this.choiceFirstCol = choiceFirstCol;
    }

    @Mutation
    setRankChoiceCol(rankChoiceCol: number){
        this.rankChoiceCol = rankChoiceCol;
    }

    @Mutation
    setViewpointFirstCol(viewpointFirstCol: number){
        this.viewpointFirstCol = viewpointFirstCol;
    }

    @Mutation
    setChoicesColumnWidth(columnWidths: any) {
        this.choicesColumnWidth = columnWidths;
    }

    // set input box value - default is 200px
    @Mutation
    setViewpointColumnWidth(columnWidth: number) {
        this.viewpointColumnWidth = columnWidth;
    }

    // each viewpoint's column width
    @Mutation
    setViewpointsColumnWidth(columnWidths: any) {
        this.viewpointsColumnWidth = columnWidths;
    }

    @Mutation
    setCommentView(view: string) {
        this.commentView = view;
    }

    @Mutation
    setSavedStateViewpoints(viewpoints: string[]) {
        this.savedStateViewpoints = viewpoints;
    }

    @Mutation
    setSavedStateChoice(choice: string) {
        this.savedStateChoice = choice;
    }

    @Mutation
    setDenominatorScoring(view: boolean) {
        this.denominatorScoring = view;
    }

    @Mutation
    setStandardizeDenominator(view: boolean) {
        this.standardizeDenominator = view;
    }

    @Mutation
    setShowFactorData(view: boolean) {
        this.showFactorData = view;
    }

    // Help & Support
    @Action
    updateShowHelpHotSpots(showHelp: boolean) {
        this.setShowHelpHotSpots(showHelp);
    }

    // set all viewpoint column width to input width
    @Action
    setAllChoicesColumnWidth(columnWidth: number) {
        const choiceModule = getModule(Choices);
        const choiceIds = choiceModule.unfilteredChoiceIds;
        let columnWidths = {};
        if (choiceIds) {
            choiceIds.forEach(choiceId => {
                columnWidths[choiceId] = columnWidth;
            })
            this.setChoicesColumnWidth(columnWidths);
        }
    }

    // set single viewpoint's column width
    @Mutation
    setSingleChoiceColumnWidth(choiceId: string, columnWidth: number) {
        this.choicesColumnWidth[choiceId] = columnWidth;
    }

    // change all viewpoint column width
    // change input box
    @Action
    changeChoiceColumnWidth(columnWidth: number) {
        this.setAllChoicesColumnWidth(columnWidth);
        this.setChoiceColumnWidth(columnWidth);
    }

    @Action
    changeChoiceFirstCol(choiceFirstCol: number) {
        this.setChoiceFirstCol(choiceFirstCol);
    }

    @Action
    changeViewpointFirstCol(viewpointFirstCol: number) {
        this.setViewpointFirstCol(viewpointFirstCol);
    }

    // set all viewpoint column width to input width
    @Action
    setAllViewpointsColumnWidth(columnWidth: number) {
        const viewpointModule = getModule(Viewpoints);
        const viewpoints = viewpointModule.decisionViewpoints;
        let columnWidths = {}
        if (viewpoints) {
            viewpoints.forEach(viewpoint => {
                columnWidths[viewpoint.viewpointId] = columnWidth;
            })
            this.setViewpointsColumnWidth(columnWidths);
        }

    }

    @Action
    resetChoiceColumnsWidth(){
        this.setChoicesColumnWidth(this.choiceColumnWidth);
    }
    @Action
    resetViewpointColumnsWidth() {
        this.setViewpointsColumnWidth(this.viewpointColumnWidth);
    }

    // set single viewpoint's column width
    @Mutation
    setSingleViewpointColumnWidth(viewpointId: string, columnWidth: number) {
        this.viewpointsColumnWidth[viewpointId] = columnWidth;
    }

    // change all viewpoint column width
    // change input box
    @Action
    changeViewpointColumnWidth(columnWidth: number) {
        this.setAllViewpointsColumnWidth(columnWidth);
        this.setViewpointColumnWidth(columnWidth);
    }

    @Mutation
    setContributingFactorVisibility(visible: boolean) {
        this.contributingFactorVisibility = visible;
    }

    @Mutation
    setChoiceVisibility(payload: UpdateVisibilityRequest) {
        Vue.set(this.choiceVisibility, payload.id, payload.value);
    }

    @Mutation
    setExpansions(payload: { [factorGroupId: string]: number }) {
        for (let id in payload) {
            Vue.set(this.expansions, id, payload[id]);
        }
    }

    @Mutation
    setDraftExpansions(payload: { [factorGroupId: string]: number }) {
        for (let id in payload) {
            Vue.set(this.draftExpansions, id, payload[id]);
        }
    }

    @Mutation
    setSelectionPath(payload: string[]) {
        this.selectionPath = payload;
    }

    @Mutation
    setDraftSelectionPath(payload: string[]) {
        this.draftSelectionPath = payload;
    }

    @Mutation
    setBreadcrumbSelectedPathItemId(id: string) {
        this.selectedPathItemId = id;
    }

    @Mutation
    setDraftSelectedPathItemId(id: string) {
        this.draftSelectedPathItemId = id;
    }

    @Mutation
    setScoreBarVisibility(visible: boolean) {
        this.scoreBarVisibility = visible;
    }

    @Mutation
    setOpenLeft(open: boolean) {
        this.openLeft = open;
    }

    @Mutation
    setOpenRight(open: boolean) {
        this.pinnedRight = open;
        this.openRight = open;
    }

    @Mutation
    setOpenRightEmbed(open: boolean) {
        this.openRight = open;
    }

    @Mutation
    setCommentVertical(vertical: boolean) {
        this.commentVertical = vertical;
    }

    @Mutation
    setPinnedRight(pinned: boolean) {
        this.pinnedRight = pinned;
    }

    @Mutation
    setPinnedLeft(pinned: boolean) {
        this.pinnedLeft = pinned;
    }

    @Mutation
    setScoringKeyVisibility(visible: boolean) {
        this.scoreBarVisibility = visible;
    }

    @Mutation
    setIconScoring(visible: boolean) {
        this.showIconScoring = visible;
    }

    @Mutation
    setSkipGroups(skip: boolean) {
        this.skipGroups = skip;
    }

    @Mutation
    setShowNonListFactors(show: boolean) {
        this.showNonListFactors = show;
    }

    // Description
    @Mutation
    setDescriptionVisibility(visible: boolean) {
        this.descriptionVisibility = visible;
    }

    @Mutation
    setRightPanelOpen(value: boolean) {
        this.rightPanelOpen = value;
    }

    @Mutation
    setSelectedElement(element: Element | null) {
        this.selectedElement = element;
    }

    @Mutation
    setActiveChoiceLists(newActiveLists: string[]) {
        this.activeChoiceLists = newActiveLists;
        let choicesModule = getModule(Choices)
        choicesModule.fetchChoices()
    }

    @Mutation
    removeActiveChoiceList(deleteListId: string) {
        Vue.delete(this.activeChoiceLists, deleteListId);

    }

    @Mutation
    setActiveFactorLists(newActiveLists: string[]) {
        this.activeFactorLists = newActiveLists;
    }

    @Mutation
    setActiveViewpointLists(newActiveLists: string[]) {
        this.activeViewpointLists = newActiveLists;
    }

    @Mutation
    removeActiveFactorList(deleteListId: string) {
        Vue.delete(this.activeFactorLists, deleteListId);

    }

    @Mutation
    removeActiveViewpointList(deleteListId: string) {
        Vue.delete(this.activeViewpointLists, deleteListId);

    }

    @Mutation
    setChoiceSort(sortId: number) {
        this.choiceSort = sortId;
    }

    @Mutation
    setViewpointSort(sortId: number) {
        this.viewpointSort = sortId;
    }

    @Mutation
    setTrimTopGroups(trim: boolean) {
        this.trimTopGroups = trim;
    }

    @Mutation
    setShowCreateChoiceDialog(value: boolean) {
        this.showCreateChoiceDialog = value;
    }

    @Mutation
    setShowCreateViewpointDialog(value: boolean) {
        this.showCreateViewpointDialog = value;
    }


    @Action
    async changeChoiceVisibility(payload: UpdateVisibilityRequest) {
        this.setChoiceVisibility(payload)
        //Save into cookie
        let decisionId = getModule(Decisions).selectedDecisionId
        let cookieKey = `choice-visibility-${decisionId}`
        Vue.$cookies.set(cookieKey, JSON.stringify(this.choiceVisibility));
    }

    @Action
    async changeScoresVisibility(visible: boolean){
        this.setScoresVisibility(visible);
    }

    @Action
    async changeKeysVisibility(visible: boolean) {
        this.setKeysVisibility(visible);
    }

    @Action
    async changeCommentsVisibility(visible: boolean) {
        this.setCommentsVisibility(visible);
    }

    @Action
    async autoCalculate(calculate: boolean) {
        this.setAutoCalc(calculate);
    }

    @Action
    async changeScoreBarVisibility(visible: boolean) {
        this.setScoreBarVisibility(visible);
        // Vue.$cookies.set(SCORE_BAR_VISIBILITY_COOKIE, visible);
    }

    // description
    @Action
    async changeDescriptionVisibility(visible: boolean) {
        this.setDescriptionVisibility(visible);
        // Vue.$cookies.set(DESCRIPTION_VISIBILITY_COOKIE, visible);
    }

    @Action
    async changeContributingFactorVisibility(visible: boolean) {
        this.setContributingFactorVisibility(visible);
        // Vue.$cookies.set(CONTRIBUTING_FACTOR_VISIBILITY_COOKIE, visible);
    }

    @Action
    async changeScoringKeyVisibility(visible: boolean) {
        this.setScoringKeyVisibility(visible);
        // Vue.$cookies.set(SCORING_KEY_VISIBILITY_COOKIE, visible);
    }

    @Action
    async changeIconScoring(visible: boolean) {
        this.setIconScoring(visible);
    }

    @Action
    async changePinnedRight(pinned: boolean) {
        this.setPinnedRight(pinned);
    }

    @Action
    async changePinnedLeft(pinned: boolean) {
        this.setPinnedLeft(pinned);
    }

    @Action
    async changeSkipGroups(val: boolean) {
        this.setSkipGroups(val);
    }

    @Action
    async changeShowNonListFactors(val: boolean) {
        this.setShowNonListFactors(val);
        // Vue.$cookies.set(SHOW_NON_LIST_FACTORS, val);
    }

    @Action
    async changeCustomRangeActive(val: boolean) {
        this.setCustomRangeActive(val)
    }

    @Action
    async changeCommentView(val: string) {
        this.setCommentView(val);
    }

    @Action
    async changeStandardizeDenominator(val: boolean) {
        this.setStandardizeDenominator(val)
    }

    @Action
    async changeDenominatorScoring(val: boolean) {
        this.setDenominatorScoring(val);
    }

    @Action
    async changeShowFactorData(val: boolean) {
        this.setShowFactorData(val);
    }

    @Action
    async showSetChoicesOnly(choiceIds: string[]) {
        let choices = getModule(Choices).choiceIds;
        if (choices) {
            for (let choiceId in choices) {
                if (choiceIds.includes(choiceId)) {
                    this.setChoiceVisibility({ id: choiceId, value: true })
                } else {
                    this.setChoiceVisibility({ id: choiceId, value: false })
                }
            }
        }
    }

    @Action
    async initializeChoiceVisibility() {
        // Get cookie to see if there is anything preset
        let decisionId = getModule(Decisions).selectedDecisionId
        let cookieKey = `choice-visibility-${decisionId}`
        let alreadyHiddenChoices: any = {};
        if (Vue.$cookies.isKey(cookieKey)) {
            alreadyHiddenChoices = Vue.$cookies.get(cookieKey);
        }
        let choices = getModule(Choices).choiceIds;
        if (choices) {
            for (let choiceId of choices) {
                if (!this.choiceVisibility.hasOwnProperty(choiceId)) {
                    if (alreadyHiddenChoices.hasOwnProperty(choiceId)) {
                        this.setChoiceVisibility({ id: choiceId, value: alreadyHiddenChoices[choiceId] })
                    } else {
                        this.setChoiceVisibility({ id: choiceId, value: true })
                    }
                }
            }
        }
    }

    // @Action
    // async initializeScoreBarVisibility() {
    //     // this.setScoreBarVisibility(Vue.$cookies.isKey(SCORE_BAR_VISIBILITY_COOKIE) ? Vue.$cookies.isKey(SCORE_BAR_VISIBILITY_COOKIE) : true);
    //     // this.setScoringKeyVisibility(Vue.$cookies.isKey(SCORING_KEY_VISIBILITY_COOKIE) ? Vue.$cookies.get(SCORING_KEY_VISIBILITY_COOKIE) : true);
    //     this.setContributingFactorVisibility(Vue.$cookies.isKey(CONTRIBUTING_FACTOR_VISIBILITY_COOKIE) ? Vue.$cookies.get(CONTRIBUTING_FACTOR_VISIBILITY_COOKIE) : true);
    //     // description
    //     // this.setDescriptionVisibility(Vue.$cookies.isKey(DESCRIPTION_VISIBILITY_COOKIE) ? Vue.$cookies.get
    //     // (DESCRIPTION_VISIBILITY_COOKIE) : true);
    //     // this.setSkipGroups(Vue.$cookies.isKey(SKIP_GROUP_COOKIE) ? Vue.$cookies.get
    //     //     (SKIP_GROUP_COOKIE) : true);
    //     // this.setShowNonListFactors(Vue.$cookies.isKey(SHOW_NON_LIST_FACTORS) ? Vue.$cookies.get
    //     //     (SHOW_NON_LIST_FACTORS) : false);
    // }

    @Action
    async updateExpansions(id: string) {
        let factorGroupIds = Object.values(getModule(Modelling).publishedModelFactorResources)
            .filter(f => f.type === 'FACTOR_GROUP')
            .map(fg => fg.id);
        let path = this.selectionPath;
        this.setSelectionPath(path);
        let newExpansions: { [factorGroupId: string]: number } = {};
        if (factorGroupIds) {
            for (let groupId of factorGroupIds) {
                if (path.includes(groupId)) {
                    newExpansions[groupId] = 0;
                }
            }
            this.setExpansions(newExpansions);
        }
    }

    @Action
    async openAllExpansions() {
        let factorGroupIds = Object.values(getModule(Modelling).publishedModelFactorResources)
            .filter(f => f.type === 'FACTOR_GROUP')
            .map(fg => fg.id);
        let newExpansions: { [factorGroupId: string]: number } = {};
        if (factorGroupIds) {
            for (let groupId of factorGroupIds) {
                newExpansions[groupId] = 0;
            }
            this.setExpansions(newExpansions);
        }
    }

    @Action
    async updateDraftExpansions(id: string) {
        let factorGroupIds = Object.values(getModule(Modelling).workingModelFactorResources)
            .filter(f => f.type === 'FACTOR_GROUP')
            .map(fg => fg.id);
        let path = this.draftSelectionPath
        this.setDraftSelectionPath(path);
        let newExpansions: { [factorGroupId: string]: number } = {};
        if (factorGroupIds) {
            for (let groupId of factorGroupIds) {
                if (path.includes(groupId)) {
                    newExpansions[groupId] = 0
                }
            }
            this.setDraftExpansions(newExpansions)
        }
    }

    @Action
    async updateSelectionPath(id: string) {
        let factorResources = getModule(Modelling).publishedModelFactorResources;
        if (id === 'M') {
            this.setSelectionPath(['M']);
            return;
        }

        let path = WorkspaceOptions.findPath(id, factorResources, factorResources['M']) || []
        this.setSelectionPath(path);
    }

    private static findPath(searchingId: string, factorResources: any, factorResource: FactorResource): string[] | undefined {
        if (factorResource?.id === searchingId) return [searchingId];
        if (factorResource.type !== 'FACTOR_GROUP') return undefined;
        let factorGroup = factorResource as FactorGroup;
        for (let childId of factorGroup.childrenIds) {
            let childPath = WorkspaceOptions.findPath(searchingId, factorResources, factorResources[childId]);
            if (childPath) {
                return [factorResource.id, ...childPath];
            }
        }
    }

    @Action
    async updateDraftSelectionPath(id: string) {
        let factorResources = getModule(Modelling).workingModelFactorResources;
        if (id === 'M') {
            this.setDraftSelectionPath(['M']);
            return;
        }
        let path = WorkspaceOptions.findPath(id, factorResources, factorResources['M']) || []
        this.setDraftSelectionPath(path);
    }

    @Action
    async selectBreadcrumbItem(id: string) {
        if (!appsModule.factorSelectionMenu) {
            await this.updateSelectionPath(id);
            this.setBreadcrumbSelectedPathItemId(id)
        }
    }

    @Action
    async selectDraftSelectionPathItem(id: string) {
        await this.updateDraftSelectionPath(id);
        this.setDraftSelectedPathItemId(id);
    }

    @Action
    async closeRightPanel() {
        this.setRightPanelOpen(false);
    }

    @Action
    async openRightPanel() {
        this.setRightPanelOpen(true);
    }

    @Action
    async selectElement(element: Element | null) {
        if (!appsModule.factorSelectionMenu) {
            this.setSelectedElement(element);
        }
    }

    @Action
    async updateSavedChoiceLists(newActiveLists: string[]) {
        let allLists = _.values(getModule(Decisions).decisionLists)
            .filter((list) => { return list.type == 'CHOICE' })
            .map((list) => { return list.listId; });

        let activeLists = newActiveLists.filter((list) => {
            if (list != '') {
                return allLists.includes(list);
            }
            return false;
        })
        this.setActiveChoiceLists(activeLists);
    }

    @Action
    async updateSavedFactorLists(newActiveLists: string[]) {
        let allLists = _.values(getModule(Decisions).decisionLists)
            .filter((list) => { return list.type == 'FACTOR' })
            .map((list) => { return list.listId; });

        let activeLists = newActiveLists.filter((list) => {
            if (list != '') {
                return allLists.includes(list);
            }
            return false;
        })

        this.setActiveFactorLists(activeLists);
    }

    @Action
    async updateSavedViewpointLists(newActiveLists: string[]) {
        let allLists = _.values(getModule(Decisions).decisionLists)
            .filter((list) => { return list.type == 'VIEWPOINT' })
            .map((list) => { return list.listId; });

        let activeLists = newActiveLists.filter((list) => {
            if (list != '') {
                return allLists.includes(list);
            }
            return false;
        })

        this.setActiveViewpointLists(activeLists);
        
        let viewpointModule = getModule(Viewpoints);
        if(!(viewpointModule.activeViewpointId in viewpointModule.decisionViewpointsMap)) {
            viewpointModule.setActiveViewpointId(null);
        }
    }

    @Action
    async appendSavedList() {
        //If id exists update
    }

    @Action
    async editSavedList() {
        //Else add to list
    }

    @Action
    async DeleteSavedListId(listId: string) {
        this.removeActiveChoiceList(listId)
    }

    @Action
    async initializeChoiceLists() {
        // Execute an @Action in Choices to filter the "choices" by the list of Id's 
        let choicesModule = getModule(Choices)
        if(this.activeChoiceLists.length > 0)
            choicesModule.filterChoices(this.activeChoiceLists);
    }

    @Action
    async changeChoiceSort(sortId: number) {
        this.setChoiceSort(sortId)
    }

    @Action
    async changeViewpointSort(sortId: number) {
        this.setViewpointSort(sortId)
    }

    @Action
    async changeTrimTopGroups(trim: boolean) {
        this.setTrimTopGroups(trim)
    }

    @Action
    async setActiveFocus(payload: {viewpointId: string, choiceId: string, factorId: string}) {
        let viewpointsModule = getModule(Viewpoints)
        let choicesModule = getModule(Choices)
        let modellingModule = getModule(Modelling)
        console.log(payload)
        if(Object.keys(viewpointsModule.decisionViewpointsMap).includes(payload.viewpointId)) {
            await viewpointsModule.selectActiveViewpoint(payload.viewpointId)
        } else {
            await viewpointsModule.selectActiveViewpoint(null)
        }

        if (Object.keys(choicesModule.choices).includes(payload.choiceId)) {
            await choicesModule.selectChoice(payload.choiceId)
        } else {
            await choicesModule.selectChoice(null)
        }

        if(Object.keys(modellingModule.publishedModelFactorResources).includes(payload.factorId)) {
            await this.selectBreadcrumbItem(payload.factorId)
        } else {
            await this.selectBreadcrumbItem('M')
        }


        
    }

    @Action
    async saveState() {
        let viewpointModule = getModule(Viewpoints);
        let choiceModule = getModule(Choices);

        //Save selected viewpoints and active choice
        let savedViewpoints = _.cloneDeep(viewpointModule.selectedViewpointIds);
        let savedChoice = _.cloneDeep(choiceModule.selectedChoiceId)
        this.setSavedStateViewpoints(savedViewpoints);
        this.setSavedStateChoice(savedChoice);
    }

    @Action
    async loadSavedState() {
        let viewpointModule = getModule(Viewpoints);
        let choiceModule = getModule(Choices);

        //load previously saved state of selected viewpoints and active choice
        let savedViewpoints = _.cloneDeep(this.savedStateViewpoints)
        viewpointModule.clearSelections()
        for(let viewpoint of savedViewpoints) {
            viewpointModule.selectViewpoint(viewpoint);
        }
        if(this.savedStateChoice != '') {
            choiceModule.selectChoice(this.savedStateChoice)
        }
    }

    @Action
    async loadStateFromUrl(oldUrl: string) {
        let url = _.clone(oldUrl);

        //Use regex to read selected viewpoints in url into an array
        const vpRegex = /viewpointIds=([a-zA-Z0-9\-]+)/g;
        let match = url.matchAll(vpRegex);
        let activeVpArray = [...url.matchAll(vpRegex)];
        let newActiveVps = []
        let viewpointModule = getModule(Viewpoints);
        let choiceModule = getModule(Choices);

        viewpointModule.clearSelections();

        //Check force state
        if (url.includes('createChoice=true')) {
            this.changeShowCreateChoiceDialog(true);
        }
        if (url.includes('autocreate=true')) {
            this.changeShowCreateViewpointDialog(true);
        }
        //Use regex to read selected choice in url into an array
        const choiceRegex = /choiceId=([a-zA-Z0-9\-]+)/;
        let activeChoiceMatch = url.match(choiceRegex);
        //url.match will return an array of the match with structure [choiceId=<choiceId>, <choiceId>] - choiceId in index 1 of array
        if (activeChoiceMatch && activeChoiceMatch.length > 0) {
            choiceModule.selectChoice(activeChoiceMatch[1]);
        }

        //Use regex to read active viewpoint in url into an array
        const activeVpRegex = /activeViewpointId=([a-zA-Z0-9\-]+)/;
        let activeMatch = url.match(activeVpRegex);
        if (activeMatch && activeMatch.length > 0) {
            //url.match will return an array of the match with structure [activeViewpointId=<viewpointId>, <viewpointId>] - viewpointId in index 1 of array
            viewpointModule.selectActiveViewpoint(activeMatch[1])
        }

        for(let match of activeVpArray) {
            if(match.length > 0) {
                if(match[1] in viewpointModule.decisionViewpointsMap) {
                    await viewpointModule.selectViewpoint(match[1]);
                    newActiveVps.push(match[1])
                }
            }
        }
    }

    @Action 
    changeShowCreateChoiceDialog(value: boolean) {
        this.setShowCreateChoiceDialog(value);
    }

    @Action
    changeShowCreateViewpointDialog(value: boolean) {
        this.setShowCreateViewpointDialog(value);
    }


}

const workspaceOptionsModule = getModule(WorkspaceOptions);

// Might fire too quickly and we may need to watch "decisions.decisions"
store.watch((state, getters) => state.decisions?.selectedDecisionId, () => {
    workspaceOptionsModule.clear();
})

store.watch((state, getters) => state.decisions?.selectedDecisionShareInformation, () => {
    workspaceOptionsModule.setBreadcrumbSelectedPathItemId('M');
    workspaceOptionsModule.setDraftSelectedPathItemId('M');
    //    workspaceOptionsModule.initializeScoreBarVisibility();
    workspaceOptionsModule.closeRightPanel();
})

store.watch((state, getters) => state.choices.choices, () => {
    workspaceOptionsModule.initializeChoiceVisibility();
})

store.watch((state, getters) => state.choices.unfilteredChoices, () => {
    workspaceOptionsModule.initializeChoiceLists();
})

interface UpdateVisibilityRequest {
    id: string,
    value: boolean
}

interface Element {
    id: string,
    type: 'FACTOR' | 'FACTOR_GROUP' | 'VIEWPOINT'
    isTemplate: boolean,
    choiceId?: string,
    viewpointId?: string
}

export {
    Element
}